import React from 'react'

export type DisputeTagType =
  | 'user'
  | 'deal'
  | 'invoice'
  | 'payout'
  | 'statement'

export interface DisputeTagToken {
  type: DisputeTagType
  id: string
  label: string
  href?: string
}

export interface DraftDisputeTagToken {
  localKey: string
  type: DisputeTagType
  label: string
}

const LOCAL_DRAFT_TYPE_CODE = {
  user: 'u',
  deal: 'd',
  invoice: 'i',
  payout: 'p',
  statement: 's'
} as const satisfies Record<DisputeTagType, string>

const LOCAL_DRAFT_TYPE_FROM_CODE = {
  u: 'user',
  d: 'deal',
  i: 'invoice',
  p: 'payout',
  s: 'statement'
} as const satisfies Record<string, DisputeTagType>

const DRAFT_METADATA_START = '\u2063'
const DRAFT_METADATA_END = '\u2064'
const DRAFT_METADATA_ZERO = '\u200b'
const DRAFT_METADATA_ONE = '\u200c'
const DRAFT_METADATA_SEPARATOR = '\u200d'

const DRAFT_TOKEN_MATCHER =
  /@[udips][A-Za-z0-9_-]+\|[^|]+\||@.*?\u2063[\u200b\u200c\u200d]+\u2064/g
const TOKEN_MATCHER =
  /@\[[^\]]+]|@[udips][A-Za-z0-9_-]+\|[^|]+\||@.*?\u2063[\u200b\u200c\u200d]+\u2064|https?:\/\/[^\s]+/g
const LEGACY_USER_MENTION_MATCHER = /^[0-9a-z-]+$/i
const TRAILING_URL_PUNCTUATION_MATCHER = /[.,)\]]+$/

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export const splitTrailingUrlPunctuation = (value: string) => {
  const trailingPunctuation = value.match(TRAILING_URL_PUNCTUATION_MATCHER)?.[0]

  if (!trailingPunctuation) {
    return {
      url: value,
      trailingText: ''
    }
  }

  return {
    url: value.slice(0, -trailingPunctuation.length),
    trailingText: trailingPunctuation
  }
}

export const createDisputeTagToken = ({
  type,
  id,
  label,
  href
}: DisputeTagToken) =>
  `@[${type}:${id}:${encodeURIComponent(label)}:${encodeURIComponent(
    href ?? ''
  )}]`

const encodeDraftMetadata = (value: string) =>
  value
    .split('')
    .map(character =>
      character
        .charCodeAt(0)
        .toString(2)
        .padStart(8, '0')
        .replaceAll('0', DRAFT_METADATA_ZERO)
        .replaceAll('1', DRAFT_METADATA_ONE)
    )
    .join(DRAFT_METADATA_SEPARATOR)

const decodeDraftMetadata = (value: string) => {
  if (!value) return null

  try {
    const decoded = value
      .split(DRAFT_METADATA_SEPARATOR)
      .map(chunk =>
        String.fromCharCode(
          Number.parseInt(
            chunk
              .replaceAll(DRAFT_METADATA_ZERO, '0')
              .replaceAll(DRAFT_METADATA_ONE, '1'),
            2
          )
        )
      )
      .join('')

    return decoded || null
  } catch {
    return null
  }
}

export const createDraftDisputeTagToken = ({
  localKey,
  type,
  label
}: DraftDisputeTagToken) =>
  `@${label}${DRAFT_METADATA_START}${encodeDraftMetadata(
    `${LOCAL_DRAFT_TYPE_CODE[type]}:${localKey}`
  )}${DRAFT_METADATA_END}`

export const parseDraftDisputeTagToken = (
  token: string
): DraftDisputeTagToken | null => {
  const match = token.match(/^@([udips])([A-Za-z0-9_-]+)\|([^|]+)\|$/)

  if (match) {
    const [, rawTypeCode, localKey, label] = match
    const typeCode = rawTypeCode as keyof typeof LOCAL_DRAFT_TYPE_FROM_CODE
    const type = LOCAL_DRAFT_TYPE_FROM_CODE[typeCode]

    if (!type || !localKey || !label) return null

    return {
      localKey,
      type,
      label
    }
  }

  const metadataStartIndex = token.indexOf(DRAFT_METADATA_START)

  if (
    !token.startsWith('@') ||
    metadataStartIndex <= 1 ||
    !token.endsWith(DRAFT_METADATA_END)
  ) {
    return null
  }

  const label = token.slice(1, metadataStartIndex)
  const metadata = token.slice(
    metadataStartIndex + DRAFT_METADATA_START.length,
    -DRAFT_METADATA_END.length
  )
  const decodedMetadata = decodeDraftMetadata(metadata)

  if (!decodedMetadata) return null

  const [rawTypeCode, localKey] = decodedMetadata.split(':')
  const typeCode = rawTypeCode as keyof typeof LOCAL_DRAFT_TYPE_FROM_CODE
  const type = LOCAL_DRAFT_TYPE_FROM_CODE[typeCode]

  if (!type || !localKey || !label) return null

  return {
    localKey,
    type,
    label
  }
}

export const parseDisputeTagToken = (
  token: string
): DisputeTagToken | { type: 'legacy-user'; id: string } | null => {
  if (!token.startsWith('@[') || !token.endsWith(']')) return null

  const body = token.slice(2, -1)

  if (LEGACY_USER_MENTION_MATCHER.test(body)) {
    return {
      type: 'legacy-user',
      id: body
    }
  }

  const [type, id, encodedLabel, encodedHref = ''] = body.split(':')

  if (
    type !== 'user' &&
    type !== 'deal' &&
    type !== 'invoice' &&
    type !== 'payout' &&
    type !== 'statement'
  ) {
    return null
  }

  if (!id || encodedLabel == null) return null

  return {
    type,
    id,
    label: safeDecode(encodedLabel),
    href: encodedHref ? safeDecode(encodedHref) : undefined
  }
}

export const extractMentionIdsFromComment = (content: string) => {
  const mentionIds = new Set<string>()

  content.match(/@\[[^\]]+]/g)?.forEach(token => {
    const parsed = parseDisputeTagToken(token)

    if (!parsed) return

    if (parsed.type === 'legacy-user') {
      mentionIds.add(parsed.id)
      return
    }

    if (parsed.type === 'user') {
      mentionIds.add(parsed.id)
    }
  })

  return [...mentionIds]
}

export const serializeDraftDisputeTokens = ({
  content,
  resolveToken
}: {
  content: string
  resolveToken: (token: DraftDisputeTagToken) => string | null
}) =>
  content.replace(DRAFT_TOKEN_MATCHER, matched => {
    const parsed = parseDraftDisputeTagToken(matched)

    if (!parsed) return matched

    return resolveToken(parsed) ?? `@${parsed.label}`
  })

const getDefaultLabelForType = (type: DisputeTagType | 'legacy-user') => {
  switch (type) {
    case 'user':
    case 'legacy-user':
      return 'User'
    case 'deal':
      return 'Deal'
    case 'invoice':
      return 'Invoice'
    case 'payout':
      return 'Compensation'
    case 'statement':
      return 'Statement'
  }
}

export const getDisputeTagDisplayLabel = ({
  token,
  mentionNamesById
}: {
  token: string
  mentionNamesById?: Record<string, string>
}) => {
  const parsed = parseDisputeTagToken(token)

  if (!parsed) return null

  if (parsed.type === 'legacy-user') {
    return mentionNamesById?.[parsed.id] ?? getDefaultLabelForType(parsed.type)
  }

  return parsed.label || getDefaultLabelForType(parsed.type)
}

const renderExternalUrl = ({
  text,
  key,
  interactive
}: {
  text: string
  key: string
  interactive: boolean
}) =>
  interactive ? (
    <a
      key={key}
      href={text}
      className='font-medium text-highlight-700 underline underline-offset-2'
      target='_blank'
      rel='noreferrer'>
      {text}
    </a>
  ) : (
    <span
      key={key}
      className='font-medium text-highlight-700 underline underline-offset-2'>
      {text}
    </span>
  )

const renderTextWithLinks = ({
  content,
  keyPrefix,
  interactive
}: {
  content: string
  keyPrefix: string
  interactive: boolean
}) =>
  content.split(/(https?:\/\/[^\s]+)/g).map((part, index) => {
    if (!part) return null

    if (/^https?:\/\/[^\s]+$/.test(part)) {
      const { url, trailingText } = splitTrailingUrlPunctuation(part)

      return (
        <React.Fragment key={`${keyPrefix}_url_fragment_${index}`}>
          {renderExternalUrl({
            text: url,
            key: `${keyPrefix}_url_${index}`,
            interactive
          })}
          {trailingText}
        </React.Fragment>
      )
    }

    return (
      <React.Fragment key={`${keyPrefix}_text_${index}`}>{part}</React.Fragment>
    )
  })

const renderTaggedContent = ({
  content,
  mentionNamesById,
  interactiveExternalLinks
}: {
  content: string
  mentionNamesById?: Record<string, string>
  interactiveExternalLinks: boolean
}) => {
  const parts: React.ReactNode[] = []
  let lastIndex = 0

  for (const match of content.matchAll(TOKEN_MATCHER)) {
    const matched = match[0]
    const start = match.index ?? 0

    if (start > lastIndex) {
      parts.push(
        <React.Fragment key={`text_${lastIndex}`}>
          {renderTextWithLinks({
            content: content.slice(lastIndex, start),
            keyPrefix: `plain_${lastIndex}`,
            interactive: interactiveExternalLinks
          })}
        </React.Fragment>
      )
    }

    if (matched.startsWith('@[')) {
      const parsed = parseDisputeTagToken(matched)
      const displayLabel = getDisputeTagDisplayLabel({
        token: matched,
        mentionNamesById
      })

      if (!parsed) {
        parts.push(matched)
      } else if (parsed.type === 'legacy-user') {
        parts.push(
          <span
            key={`legacy_user_${start}`}
            className='font-semibold text-teal-600'>
            @{displayLabel}
          </span>
        )
      } else if (parsed.type === 'user') {
        parts.push(
          <span key={`user_${start}`} className='font-semibold text-teal-600'>
            @{displayLabel}
          </span>
        )
      } else {
        parts.push(
          <span
            key={`entity_${start}`}
            className='font-semibold text-highlight-700'>
            @{displayLabel}
          </span>
        )
      }
    } else if (matched.startsWith('@')) {
      const parsedDraftToken = parseDraftDisputeTagToken(matched)

      if (!parsedDraftToken) {
        parts.push(matched)
      } else if (parsedDraftToken.type === 'user') {
        parts.push(
          <span
            key={`draft_user_${start}`}
            className='font-semibold text-teal-600'>
            @{parsedDraftToken.label}
          </span>
        )
      } else {
        parts.push(
          <span
            key={`draft_entity_${start}`}
            className='font-semibold text-highlight-700'>
            @{parsedDraftToken.label}
          </span>
        )
      }
    } else {
      const { url, trailingText } = splitTrailingUrlPunctuation(matched)

      parts.push(
        <React.Fragment key={`url_fragment_${start}`}>
          {renderExternalUrl({
            text: url,
            key: `url_${start}`,
            interactive: interactiveExternalLinks
          })}
          {trailingText}
        </React.Fragment>
      )
    }

    lastIndex = start + matched.length
  }

  if (lastIndex < content.length) {
    parts.push(
      <React.Fragment key={`tail_${lastIndex}`}>
        {renderTextWithLinks({
          content: content.slice(lastIndex),
          keyPrefix: `tail_${lastIndex}`,
          interactive: interactiveExternalLinks
        })}
      </React.Fragment>
    )
  }

  return parts
}

export const renderTaggedCommentContent = ({
  content,
  mentionNamesById
}: {
  content: string
  mentionNamesById?: Record<string, string>
}) =>
  renderTaggedContent({
    content,
    mentionNamesById,
    interactiveExternalLinks: true
  })

export const renderTaggedDraftContent = ({
  content,
  mentionNamesById
}: {
  content: string
  mentionNamesById?: Record<string, string>
}) =>
  renderTaggedContent({
    content,
    mentionNamesById,
    interactiveExternalLinks: false
  })
